import React, { useState, useEffect, useRef, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { fetchBrand, imgUrl } from "../api";

// ─── useInView ────────────────────────────────────────────────────────────────
function useInView(threshold = 0.1, initialValue = false) {
  const ref = useRef(null);
  const [inView, setInView] = useState(initialValue);
  useEffect(() => {
    if (initialValue) return; // already visible, skip observer
    const obs = new IntersectionObserver(
      ([e]) => {
        if (e.isIntersecting) {
          setInView(true);
          obs.disconnect();
        }
      },
      { threshold, rootMargin: "0px 0px -20px 0px" },
    );
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, [threshold, initialValue]);
  return [ref, inView];
}

// ─── Lightbox ─────────────────────────────────────────────────────────────────
function Lightbox({ images, startIndex, onClose }) {
  const [current, setCurrent] = useState(startIndex);
  const prev = useCallback(
    () => setCurrent((c) => (c - 1 + images.length) % images.length),
    [images.length],
  );
  const next = useCallback(
    () => setCurrent((c) => (c + 1) % images.length),
    [images.length],
  );

  useEffect(() => {
    const fn = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowLeft") prev();
      if (e.key === "ArrowRight") next();
    };
    window.addEventListener("keydown", fn);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", fn);
      document.body.style.overflow = "";
    };
  }, [onClose, prev, next]);

  return (
    <div
      className="fixed inset-0 z-[200] bg-black/95 flex flex-col items-center justify-center"
      style={{ animation: "lbFade 0.2s ease" }}
      onClick={onClose}
    >
      <div
        className="absolute top-0 left-0 right-0 flex items-center justify-between px-5 py-4 z-10"
        onClick={(e) => e.stopPropagation()}
      >
        <span className="font-body text-white/40 text-xs tracking-widest uppercase">
          {current + 1} / {images.length}
        </span>
        <button
          onClick={onClose}
          className="w-9 h-9 rounded-full border border-white/20 hover:border-gold hover:bg-gold flex items-center justify-center text-white/60 hover:text-white transition-all duration-200"
        >
          <svg
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.5"
          >
            <path d="M18 6L6 18M6 6l12 12" />
          </svg>
        </button>
      </div>
      <div
        className="relative w-full max-w-5xl px-14 md:px-20"
        onClick={(e) => e.stopPropagation()}
      >
        <img
          key={current}
          src={images[current]}
          alt=""
          className="w-full max-h-[72vh] object-contain rounded-xl"
          style={{ animation: "lbScale 0.25s ease" }}
        />
      </div>
      {[
        { fn: prev, icon: "M15 18l-6-6 6-6", pos: "left-3 md:left-6" },
        { fn: next, icon: "M9 18l6-6-6-6", pos: "right-3 md:right-6" },
      ].map(({ fn, icon, pos }, i) => (
        <button
          key={i}
          onClick={(e) => {
            e.stopPropagation();
            fn();
          }}
          className={`absolute ${pos} top-1/2 -translate-y-1/2 w-10 h-10 md:w-12 md:h-12 rounded-full bg-white/10 hover:bg-gold border border-white/20 hover:border-gold text-white flex items-center justify-center transition-all duration-200`}
        >
          <svg
            width="15"
            height="15"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.5"
          >
            <path d={icon} />
          </svg>
        </button>
      ))}
      <div
        className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2 px-4 overflow-x-auto max-w-full"
        onClick={(e) => e.stopPropagation()}
      >
        {images.map((img, i) => (
          <button
            key={i}
            onClick={() => setCurrent(i)}
            className={`flex-shrink-0 w-14 h-10 rounded-lg overflow-hidden border-2 transition-all duration-200 ${i === current ? "border-gold scale-110" : "border-transparent opacity-40 hover:opacity-70"}`}
          >
            <img src={img} alt="" className="w-full h-full object-cover" />
          </button>
        ))}
      </div>
    </div>
  );
}

// ─── Gallery ──────────────────────────────────────────────────────────────────
function Gallery({ images }) {
  const [ref, inView] = useInView(0.1);
  const [current, setCurrent] = useState(0);
  const [paused, setPaused] = useState(false);
  const [lightbox, setLightbox] = useState(null);
  const trackRef = useRef(null);

  const getVis = useCallback(
    () => (window.innerWidth < 640 ? 1 : window.innerWidth < 1024 ? 2 : 3),
    [],
  );
  const [vis, setVis] = useState(getVis);
  useEffect(() => {
    const h = () => setVis(getVis());
    window.addEventListener("resize", h, { passive: true });
    return () => window.removeEventListener("resize", h);
  }, [getVis]);

  const maxIdx = Math.max(0, images.length - vis);
  const goTo = useCallback(
    (idx) => {
      const n = Math.max(0, Math.min(idx, maxIdx));
      setCurrent(n);
      if (trackRef.current) {
        const cw = (trackRef.current.offsetWidth + 16) / vis;
        trackRef.current.scrollTo({ left: n * cw, behavior: "smooth" });
      }
    },
    [maxIdx, vis],
  );

  useEffect(() => {
    if (paused) return;
    const t = setInterval(() => {
      setCurrent((prev) => {
        const next = prev >= maxIdx ? 0 : prev + 1;
        if (trackRef.current) {
          const cw = (trackRef.current.offsetWidth + 16) / vis;
          trackRef.current.scrollTo({ left: next * cw, behavior: "smooth" });
        }
        return next;
      });
    }, 2500);
    return () => clearInterval(t);
  }, [paused, maxIdx, vis]);

  const dragStart = useRef(null);

  if (!images || images.length === 0) return null;

  return (
    <section
      ref={ref}
      className="bg-white py-4 md:py-8 px-4 sm:px-8 md:px-12 lg:px-20"
    >
      <div className="max-w-7xl mx-auto">
        <div
          className={`mb-8 md:mb-10 transition-all duration-700 ${inView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}
        >
          <h2
            className="font-display font-normal text-stone-900"
            style={{ fontSize: "clamp(1.8rem, 3.5vw, 2.6rem)" }}
          >
            Gallery
          </h2>
        </div>
        <div
          className="relative"
          onMouseEnter={() => setPaused(true)}
          onMouseLeave={() => setPaused(false)}
        >
          <div
            ref={trackRef}
            className="flex overflow-hidden"
            style={{ gap: "16px", cursor: "grab", userSelect: "none", scrollBehavior: 'smooth' }}
            onPointerDown={(e) => {
              dragStart.current = e.clientX;
            }}
            onPointerUp={(e) => {
              if (!dragStart.current) return;
              const d = dragStart.current - e.clientX;
              if (Math.abs(d) > 40) goTo(current + (d > 0 ? 1 : -1));
              dragStart.current = null;
            }}
            onPointerLeave={() => {
              dragStart.current = null;
            }}
          >
            {images.map((img, i) => (
              <div
                key={i}
                onClick={() => setLightbox(i)}
                className={`flex-none transition-all duration-600 ${inView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}
                style={{
                  width: `calc(${100 / vis}% - ${(16 * (vis - 1)) / vis}px)`,
                  transitionDelay: `${i * 70}ms`,
                }}
              >
                <div
                  className="relative overflow-hidden rounded-br-[40px] group cursor-pointer"
                  style={{ aspectRatio: "4/5" }}
                >
                  <img
                    src={img}
                    alt={`Gallery ${i + 1}`}
                    draggable="false"
                    loading="lazy"
                    className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/25 transition-all duration-300 flex items-center justify-center">
                    <div className="opacity-0 group-hover:opacity-100 transition-all duration-300 transform scale-75 group-hover:scale-100 w-11 h-11 rounded-full bg-white/20 backdrop-blur-sm border border-white/40 flex items-center justify-center">
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="white"
                        strokeWidth="2"
                      >
                        <path d="M8 3H5a2 2 0 0 0-2 2v3m18 0V5a2 2 0 0 0-2-2h-3m0 18h3a2 2 0 0 0 2-2v-3M3 16v3a2 2 0 0 0 2 2h3" />
                      </svg>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
          <button
            onClick={() => goTo(current - 1)}
            className={`absolute left-0 top-1/2 -translate-y-1/2 -translate-x-4 md:-translate-x-5 z-10 w-10 h-10 rounded-full bg-stone-900 hover:bg-gold text-white flex items-center justify-center shadow-xl transition-all duration-200 ${current === 0 ? "opacity-0 pointer-events-none" : "opacity-100"}`}
          >
            <svg
              width="15"
              height="15"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
            >
              <path d="M15 18l-6-6 6-6" />
            </svg>
          </button>
          <button
            onClick={() => goTo(current + 1)}
            className={`absolute right-0 top-1/2 -translate-y-1/2 translate-x-4 md:translate-x-5 z-10 w-10 h-10 rounded-full bg-stone-900 hover:bg-gold text-white flex items-center justify-center shadow-xl transition-all duration-200 ${current >= maxIdx ? "opacity-0 pointer-events-none" : "opacity-100"}`}
          >
            <svg
              width="15"
              height="15"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
            >
              <path d="M9 18l6-6-6-6" />
            </svg>
          </button>
        </div>
        {maxIdx > 0 && (
          <div className="flex justify-center gap-2 mt-6">
            {Array.from({ length: maxIdx + 1 }).map((_, i) => (
              <button
                key={i}
                onClick={() => goTo(i)}
                className={`h-1 rounded-full transition-all duration-300 ${i === current ? "w-7 bg-stone-800" : "w-2 bg-stone-300 hover:bg-stone-400"}`}
              />
            ))}
          </div>
        )}
      </div>
      {lightbox !== null && (
        <Lightbox
          images={images}
          startIndex={lightbox}
          onClose={() => setLightbox(null)}
        />
      )}
    </section>
  );
}

// ─── Main ─────────────────────────────────────────────────────────────────────
export default function BrandDetailPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [brand, setBrand] = useState(null);
  const [loading, setLoading] = useState(true);

  const [heroRef, heroInView] = useInView(0, true);
  const [statsRef, statsInView] = useInView(0, true);
  const [whyRef, whyInView] = useInView(0, true);

  useEffect(() => {
    setLoading(true);
    window.scrollTo({ top: 0 });

    fetchBrand(slug)
      .then((d) => setBrand(d.data))
      .catch(() => setBrand(null))
      .finally(() => setLoading(false));
  }, [slug]);

  // Gallery images — convert paths to full URLs
  const galleryImages = brand?.gallery?.map((g) => imgUrl(g)) || [];

  if (loading)
    return (
      <div className="min-h-screen bg-white pt-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 md:px-12 lg:px-20 py-12 md:py-16">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-10 animate-pulse">
            <div className="bg-stone-100 rounded-2xl h-72 md:h-96" />
            <div className="space-y-4 pt-4">
              <div className="h-5 bg-stone-100 rounded w-1/3" />
              <div className="h-10 bg-stone-100 rounded w-2/3" />
              <div className="h-4 bg-stone-100 rounded w-full mt-4" />
              <div className="h-4 bg-stone-100 rounded w-5/6" />
            </div>
          </div>
        </div>
      </div>
    );

  if (!brand)
    return (
      <div className="min-h-screen bg-white pt-20 flex items-center justify-center text-center px-4">
        <div>
          <p className="font-display text-stone-400 text-2xl font-light mb-4">
            Brand not found
          </p>
          <button
            onClick={() => navigate("/brands")}
            className="font-body text-[11px] tracking-widest uppercase text-gold hover:text-stone-900 transition-colors"
          >
            ← Back to Brands
          </button>
        </div>
      </div>
    );

  return (
    <div className="min-h-screen bg-white pt-20">
      {/* Hero + About */}
      <section
        ref={heroRef}
        className="px-4 sm:px-8 md:px-12 lg:px-20 py-10 md:py-16"
      >
        <div className="max-w-7xl mx-auto">
          <button
            onClick={() => navigate("/brands")}
            className={`flex items-center gap-2 font-body text-[10px] tracking-[0.2em] uppercase text-stone-400 hover:text-gold transition-all duration-300 mb-8 group ${heroInView ? "opacity-100 translate-x-0" : "opacity-0 -translate-x-4"}`}
            style={{ transitionDuration: "600ms" }}
          >
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <path d="M19 12H5M12 5l-7 7 7 7" />
            </svg>
            All Brands
          </button>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-14 items-start">
            {/* Image */}
            <div
              className={`transition-all duration-700 ${heroInView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`}
            >
              <div className="relative">
                <div className="absolute -bottom-3 -right-3 w-full h-full border border-gold/25 rounded-br-[40px] pointer-events-none" />
                <img
                  src={
                    brand.heroImg ? imgUrl(brand.heroImg) : "images/french1.jpg"
                  }
                  alt={brand.name}
                  className="relative z-10 w-full h-72 sm:h-96 md:h-[520px] object-cover rounded-br-[40px]"
                />
                <div className="absolute top-4 left-4 z-20">
                  <span className="font-body text-white/90 text-[9px] tracking-[0.15em] uppercase bg-black/35 backdrop-blur-sm px-3 py-1.5 rounded-full">
                    {brand.category?.name || ""}
                  </span>
                </div>
              </div>
            </div>

            {/* Info */}
            <div
              className={`transition-all duration-700 delay-150 ${heroInView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`}
            >
              <div className="mb-6 md:mb-8 p-2 bg-[#F4EEE6] rounded-sm rounded-br-[40px]">
                <h1
                  className="font-display font-normal text-stone-900 leading-tight mb-1"
                  style={{ fontSize: "clamp(2rem, 5vw, 3.5rem)" }}
                >
                  {brand.name}
                </h1>
                {brand.tagline && (
                  <p className="font-body text-stone-400 text-sm">
                    {brand.tagline}
                  </p>
                )}
              </div>
              <div className="p-2">
                <h2 className="font-display font-semibold text-stone-900 text-xl md:text-2xl mb-3">
                  About {brand.name}
                </h2>
                <p className="font-body text-stone-500 text-sm md:text-[15px] leading-relaxed">
                  {brand.about}
                </p>
              </div>
              <button
                onClick={() => navigate("/contact")}
                className="mt-8 group flex items-center gap-3 bg-gold hover:bg-stone-900 text-white font-body text-[11px] font-semibold tracking-[0.2em] uppercase px-7 py-3.5 rounded-br-[20px] transition-all duration-300 hover:-translate-y-0.5 hover:shadow-lg"
              >
                Enquire Now
                <svg
                  width="13"
                  height="13"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.5"
                >
                  <path d="M5 12h14M12 5l7 7-7 7" />
                </svg>
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Stats */}
      {brand.stats?.length > 0 && (
        <section
          ref={statsRef}
          className="px-4 sm:px-8 md:px-12 lg:px-20 py-10 md:py-14 bg-white border-t border-stone-100"
        >
          <div className="max-w-7xl mx-auto">
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3 md:gap-4">
              {brand.stats.map((stat, i) => (
                <div
                  key={stat.label}
                  className={`group relative bg-[#f5ede4] rounded-br-[40px] px-5 md:px-6 py-5 md:py-6 transition-all duration-300 hover:shadow-md hover:-translate-y-0.5 hover:bg-[#eddfd3] ${statsInView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}
                  style={{ transitionDelay: `${i * 75}ms` }}
                >
                  <p className="font-body text-stone-600 text-sm mb-2 leading-snug">
                    {stat.label}
                  </p>
                  <div className="w-full h-px bg-stone-300/60 mb-3 group-hover:bg-gold/40 transition-colors duration-300" />
                  <p className="font-body text-stone-800 text-base md:text-lg font-medium leading-snug">
                    {stat.value}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Why FBS */}
      {brand.whyFBS?.length > 0 && (
        <section
          ref={whyRef}
          className="px-4 sm:px-8 md:px-12 lg:px-20 py-4 md:py-8 border-t border-stone-100"
        >
          <div className="max-w-7xl mx-auto">
            <h2
              className={`font-display font-normal text-stone-900 mb-6 md:mb-8 transition-all duration-700 ${whyInView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}
              style={{ fontSize: "clamp(1.8rem, 3.5vw, 2.8rem)" }}
            >
              Why Choose FBS?
            </h2>
            <div className="flex flex-col gap-4">
              {brand.whyFBS.map((text, i) => (
                <p
                  key={i}
                  className={`font-body text-stone-500 text-sm md:text-base leading-relaxed max-w-3xl transition-all duration-700 ${whyInView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}
                  style={{ transitionDelay: `${i * 120}ms` }}
                >
                  {text}
                </p>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Gallery */}
      <Gallery images={galleryImages} />

      <style>{`
        @keyframes lbFade  { from { opacity:0 } to { opacity:1 } }
        @keyframes lbScale { from { opacity:0; transform:scale(0.95) } to { opacity:1; transform:scale(1) } }
      `}</style>
    </div>
  );
}
